// New Ship-to-Patient exception flow.
//
// Steps:
//   1. Patient + reason (missed_bedside | out_of_stock | other)
//   2. Prescriptions — server-checked eligibility, only ships_eligible checkable
//   3. Address — confirm read-only PMS snapshot
//   4. Weight — manual + WebHID (ScaleReader component)
//   5. Rate shop — pick carrier + service
//   6. Payment — record PMS-POS receipt # (cashier charged the card-on-file there)
//   7. Buy label — gated on payment; shows tracking + label PDF link
//
// Eligibility is enforced by the server on every privileged step (rate, label).
// We never trust the client.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import ScaleReader from '../components/ScaleReader.jsx';
import { useTenantHref } from '../lib/tenantPath.jsx';

export default function ShipmentNew() {
  const tHref = useTenantHref();
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1 state
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [reason, setReason] = useState('missed_bedside');

  // Step 2 state
  const [prescriptions, setPrescriptions] = useState([]);
  const [picked, setPicked] = useState({});

  // After create
  const [shipment, setShipment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Step 4 weight
  const [weightOz, setWeightOz] = useState(null);
  const [weightSrc, setWeightSrc] = useState('manual');

  // Step 5 rates
  const [rates, setRates] = useState([]);
  const [chosenRate, setChosenRate] = useState(null);

  // Step 6 payment
  const [pmsRef, setPmsRef] = useState('');
  const [pmsAmount, setPmsAmount] = useState('');

  // ----- Step 1: patient autocomplete + reason ------------------------------
  async function searchPatients() {
    if (!patientQuery || patientQuery.length < 2) { setPatientResults([]); return; }
    try {
      const r = await api.patients({ search: patientQuery, limit: 10 });
      setPatientResults(r.patients || []);
    } catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => {
    const t = setTimeout(searchPatients, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [patientQuery]);

  async function loadPrescriptions(p) {
    setBusy(true); setErr(null);
    try {
      const r = await api.patient(p.id);
      setPrescriptions(r.prescriptions || []);
      // Pre-pick eligible-and-active ones
      const initial = {};
      for (const rx of r.prescriptions || []) {
        if (rx.ships_eligible && ['filled', 'verified', 'dispensed'].includes(rx.status)) {
          initial[rx.id] = true;
        }
      }
      setPicked(initial);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  // ----- Step 2 -> create draft --------------------------------------------
  async function createDraft() {
    setBusy(true); setErr(null);
    try {
      const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
      if (!ids.length) { setErr('Pick at least one eligible prescription.'); setBusy(false); return; }
      const r = await api.createShipment({
        patient_id: patient.id, reason, prescription_ids: ids,
      });
      setShipment(r.shipment);
      setStep(3);
    } catch (e) {
      if (e.body?.error === 'ineligible') {
        setErr('Some prescriptions are not eligible: ' +
          e.body.ineligible.map(i => `${i.drug_name} (${i.reason})`).join(', '));
      } else if (e.body?.error === 'address_incomplete') {
        setErr(e.body.message + ' — update the patient record in PMS first.');
      } else {
        setErr(e.body?.message || e.message);
      }
    } finally { setBusy(false); }
  }

  // ----- Step 4 -> rate-shop -----------------------------------------------
  async function doRateShop() {
    setBusy(true); setErr(null);
    try {
      const r = await api.shipmentRate(shipment.id, {
        weight_oz: weightOz, weight_source: weightSrc,
      });
      setRates(r.rates || []);
      setShipment({ ...shipment, weight_oz: weightOz, weight_source: weightSrc, ep_shipment_id: r.ep_shipment_id });
      setStep(5);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  async function pickRate(r) {
    setBusy(true); setErr(null);
    try {
      const upd = await api.shipmentSelectRate(shipment.id, {
        ep_rate_id: r.id, carrier: r.carrier, service: r.service, rate_cents: r.rate_cents,
      });
      setShipment(upd.shipment);
      setChosenRate(r);
      setStep(6);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  async function confirmPayment() {
    setBusy(true); setErr(null);
    try {
      if (!pmsRef.trim()) { setErr('PMS-POS receipt # required.'); setBusy(false); return; }
      const upd = await api.shipmentPayment(shipment.id, {
        pms_payment_ref: pmsRef.trim(),
        pms_payment_amount: Number(pmsAmount || 0),
      });
      setShipment(upd.shipment);
      setStep(7);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  async function buyLabel() {
    setBusy(true); setErr(null);
    try {
      const upd = await api.shipmentBuyLabel(shipment.id);
      setShipment(upd.shipment);
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">New Shipment</h1>
          <p className="text-sm text-slate-500">Ship-to-Patient exception flow</p>
        </div>
        <Link to={tHref('/shipments')} className="text-sm hover:underline" style={{ color: 'var(--brand-primary)' }}>
          ← All shipments
        </Link>
      </div>

      <Stepper current={step} steps={['Patient & reason', 'Rxs', 'Address', 'Weight', 'Rate', 'Payment', 'Label']} />

      {err && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{err}</div>}

      {step === 1 && (
        <div className="card p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-600 mb-1">Reason</label>
            <div className="flex gap-2">
              <ReasonChip label="Missed bedside delivery" value="missed_bedside" current={reason} onClick={setReason} />
              <ReasonChip label="Out of stock" value="out_of_stock" current={reason} onClick={setReason} />
              <ReasonChip label="Other" value="other" current={reason} onClick={setReason} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">Patient</label>
            {!patient ? (
              <>
                <input className="input" placeholder="Search by name or MRN…"
                  value={patientQuery} onChange={e => setPatientQuery(e.target.value)} autoFocus />
                {patientResults.length > 0 && (
                  <ul className="mt-2 border border-slate-200 rounded divide-y divide-slate-100 bg-white">
                    {patientResults.map(p => (
                      <li key={p.id} className="px-3 py-2 hover:bg-slate-50 cursor-pointer"
                          onClick={() => { setPatient(p); loadPrescriptions(p); setStep(2); }}>
                        <div className="font-medium">{p.first_name} {p.last_name}</div>
                        <div className="text-xs text-slate-500 font-mono">{p.mrn} · room {p.room_number || '—'}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded p-3">
                <div>
                  <div className="font-medium">{patient.first_name} {patient.last_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{patient.mrn}</div>
                </div>
                <button className="text-xs text-slate-600 hover:underline"
                  onClick={() => { setPatient(null); setPatientResults([]); setPatientQuery(''); }}>Change</button>
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500">
            Address is read from PMS automatically once a patient is selected.
            We never collect or store card data — copay is charged on PMS-POS.
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Pick prescriptions to ship</div>
          {prescriptions.length === 0 && <div className="text-sm text-slate-500">No prescriptions on this patient.</div>}
          <ul className="divide-y divide-slate-100">
            {prescriptions.map(rx => {
              const ineligibleReason = !rx.ships_eligible &&
                (rx.high_risk ? 'high-risk medication'
                : rx.counseling_required ? 'counseling required'
                : rx.controlled_substance ? 'controlled substance'
                : rx.refrigerated ? 'refrigeration / cold-chain'
                : 'not eligible');
              return (
                <li key={rx.id} className="py-2 flex items-start gap-3">
                  <input type="checkbox" className="mt-1"
                    disabled={!rx.ships_eligible}
                    checked={!!picked[rx.id]}
                    onChange={e => setPicked(p => ({ ...p, [rx.id]: e.target.checked }))} />
                  <div className="flex-1">
                    <div className="font-medium">{rx.drug_name} {rx.strength}</div>
                    <div className="text-xs text-slate-500">
                      {rx.rx_number || '—'} · status {rx.status}
                      {rx.copay_amount != null && <> · copay ${Number(rx.copay_amount).toFixed(2)}</>}
                    </div>
                    {!rx.ships_eligible && (
                      <div className="text-xs text-amber-700 mt-0.5">Cannot ship: {ineligibleReason}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button className="btn-primary" disabled={busy} onClick={createDraft}>
              {busy ? 'Creating…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && shipment && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Confirm shipping address (from PMS)</div>
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-sm font-mono whitespace-pre-line">
            {shipment.to_name}
            {'\n'}{shipment.to_street1}{shipment.to_street2 ? `, ${shipment.to_street2}` : ''}
            {'\n'}{shipment.to_city}, {shipment.to_state} {shipment.to_zip}
            {shipment.to_phone ? `\n${shipment.to_phone}` : ''}
          </div>
          <div className="text-xs text-slate-500">
            If anything looks wrong, update the patient record in your PMS before continuing — we don't edit addresses on the shipment after creation.
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => nav(tHref('/shipments'))}>Cancel</button>
            <button className="btn-primary" onClick={() => setStep(4)}>Address looks correct →</button>
          </div>
        </div>
      )}

      {step === 4 && shipment && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Weigh the package</div>
          <ScaleReader
            value={weightOz}
            source={weightSrc}
            onChange={(v, src) => { setWeightOz(v); setWeightSrc(src); }}
          />
          <div className="text-xs text-slate-500">
            Tip: manual entry is always available. The "Read from scale" button uses your browser's WebHID API to read a connected USB POS scale (DYMO M25, Mettler Toledo PS60, etc.) — Chrome or Edge over HTTPS.
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStep(3)}>Back</button>
            <button className="btn-primary" disabled={!weightOz || busy} onClick={doRateShop}>
              {busy ? 'Rating…' : 'Get rates'}
            </button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Pick carrier + service</div>
          {rates.length === 0 && <div className="text-sm text-slate-500">No rates returned.</div>}
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded">
            {rates.sort((a, b) => a.rate_cents - b.rate_cents).map(r => (
              <li key={r.id} className="px-3 py-2 flex items-center justify-between hover:bg-slate-50">
                <div>
                  <div className="font-medium">{r.carrier} · {r.service}</div>
                  <div className="text-xs text-slate-500">
                    {r.delivery_days != null ? `${r.delivery_days}-day estimate` : 'delivery time varies'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="font-mono">${(r.rate_cents / 100).toFixed(2)}</div>
                  <button className="btn-primary" disabled={busy} onClick={() => pickRate(r)}>Select</button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStep(4)}>Back</button>
          </div>
        </div>
      )}

      {step === 6 && (
        <div className="card p-5 space-y-3">
          <div className="font-medium">Confirm copay charged on PMS-POS</div>
          <div className="text-sm text-slate-600">
            Cashier: charge the card-on-file in your PMS POS for the patient's copay, then enter the receipt number below. We do <strong>not</strong> process the card here.
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-xs text-slate-600">PMS-POS receipt / transaction #</span>
              <input className="input font-mono" value={pmsRef} onChange={e => setPmsRef(e.target.value)} />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-slate-600">Amount charged ($)</span>
              <input type="number" min="0" step="0.01" className="input font-mono"
                value={pmsAmount} onChange={e => setPmsAmount(e.target.value)} />
            </label>
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setStep(5)}>Back</button>
            <button className="btn-primary" disabled={!pmsRef.trim() || busy} onClick={confirmPayment}>
              {busy ? 'Saving…' : 'Confirm payment'}
            </button>
          </div>
        </div>
      )}

      {step === 7 && shipment && (
        <div className="card p-5 space-y-3">
          {!shipment.tracking_number ? (
            <>
              <div className="font-medium">Buy label</div>
              <div className="text-sm text-slate-600">
                {chosenRate
                  ? <>You're about to buy <strong>{chosenRate.carrier} {chosenRate.service}</strong> at <strong>${(chosenRate.rate_cents / 100).toFixed(2)}</strong>.</>
                  : 'Rate selected.'}
              </div>
              <button className="btn-primary" disabled={busy} onClick={buyLabel}>
                {busy ? 'Purchasing…' : 'Buy label'}
              </button>
            </>
          ) : (
            <>
              <div className="text-emerald-700 font-semibold">Label purchased — patient and nurse notified.</div>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Tracking number</div>
                  <div className="font-mono">{shipment.tracking_number}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Carrier</div>
                  <div>{shipment.carrier} · {shipment.service_level}</div>
                </div>
              </div>
              {shipment.label_url && (
                <a href={shipment.label_url} target="_blank" rel="noreferrer" className="btn-primary inline-block">
                  Open label PDF
                </a>
              )}
              <div className="flex gap-2 pt-2">
                <button className="btn-ghost" onClick={() => nav(`/shipments/${shipment.id}`)}>View shipment</button>
                <button className="btn-ghost" onClick={() => nav(tHref('/shipments'))}>Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stepper({ current, steps }) {
  return (
    <ol className="flex flex-wrap gap-2 text-xs">
      {steps.map((s, i) => {
        const idx = i + 1; const done = idx < current; const active = idx === current;
        return (
          <li key={s} className={`px-2 py-1 rounded ${done ? 'bg-emerald-100 text-emerald-800'
            : active ? 'text-white' : 'bg-slate-100 text-slate-600'}`}
            style={active ? { background: 'var(--brand-primary)' } : undefined}>
            {idx}. {s}
          </li>
        );
      })}
    </ol>
  );
}
function ReasonChip({ label, value, current, onClick }) {
  return (
    <button type="button" onClick={() => onClick(value)}
      className={`px-3 py-1.5 rounded text-sm border ${current === value
        ? 'border-transparent text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
      style={current === value ? { background: 'var(--brand-primary)' } : undefined}>
      {label}
    </button>
  );
}
